import React, { useState } from 'react';
import styled from 'styled-components';
import { Player, GameRole } from '../../types';
import {
  SectionCard,
  InstructionCard,
  PhaseButton,
  PlayerAvatar,
  MetaInfo,
  MetaHighlight,
} from '../../styles/SharedStyles';
import { theme } from '../../styles/theme';

import { dictionaries } from '../../utils/dictionary';

interface LastWordsProps {
  language: number;
  eliminatedPlayers: Player[];
  eliminatedPlayerNumbers: number[];
  eliminationType: 'voted' | 'killed';
  playerFauls: number[];
  playerRoles?: GameRole[];
  onComplete: () => void;
  onUndo?: () => void;
}

const LastWordsCard = styled(SectionCard)`
  background: linear-gradient(145deg, rgba(18, 28, 40, 0.96), rgba(12, 20, 32, 0.96));
  border: 1px solid rgba(0, 186, 255, 0.35);
  box-shadow: 0 20px 60px rgba(0, 186, 255, 0.15);
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const CardTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #dcd4ff;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button.attrs({ type: 'button' })`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  font-size: 1.25rem;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
  }
`;

const PlayerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};

  @media (min-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: center;
  }
`;

const PlayerCard = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  background: rgba(0, 186, 255, 0.08);
  border: 1px solid rgba(0, 186, 255, 0.25);

  @media (min-width: ${theme.breakpoints.mobile}) {
    min-width: 200px;
  }
`;

const PlayerAvatarLarge = styled(PlayerAvatar)`
  width: 64px;
  height: 64px;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid rgba(0, 186, 255, 0.5);
`;

const PlayerNameLarge = styled.strong`
  font-size: 1.1rem;
  text-align: center;
`;

const PlayerNote = styled.small`
  font-size: 0.85rem;
  opacity: 0.75;
  text-align: center;
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const ProgressLabel = styled.small`
  font-size: 0.85rem;
  opacity: 0.75;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${theme.borderRadius.round};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, rgba(0, 186, 255, 0.8), rgba(124, 93, 255, 0.8));
  border-radius: ${theme.borderRadius.round};
  transition: width ${theme.transitions.normal};
`;

const InstructionsCard = styled(InstructionCard)`
  display: none;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: ${theme.colors.text.secondary};

  ul {
    margin: 0.5rem 0 0;
    padding-left: 1.25rem;
  }

  li {
    margin-top: 0.25rem;
    font-size: 0.85rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
  justify-content: center;
  margin-top: ${theme.spacing.lg};
`;

const UndoButton = styled(PhaseButton)`
  background: linear-gradient(135deg, rgba(255, 87, 87, 0.25), rgba(200, 40, 40, 0.35));
  border-color: rgba(255, 87, 87, 0.45);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(255, 87, 87, 0.35), rgba(200, 40, 40, 0.45));
  }
`;

const LastWords: React.FC<LastWordsProps> = ({
  language,
  eliminatedPlayers,
  eliminatedPlayerNumbers,
  eliminationType,
  playerFauls,
  playerRoles,
  onComplete,
  onUndo,
}) => {
  const [currentPlayerIndex, setCurrentPlayerIndex] = useState(0);
  const dict = dictionaries[language].ph;

  const currentPlayer = eliminatedPlayers[currentPlayerIndex];
  const currentPlayerNumber = eliminatedPlayerNumbers[currentPlayerIndex];
  const isLastPlayer = currentPlayerIndex === eliminatedPlayers.length - 1;
  const progress = ((currentPlayerIndex + 1) / eliminatedPlayers.length) * 100;

  const handleNext = () => {
    if (isLastPlayer) {
      onComplete();
    } else {
      setCurrentPlayerIndex(currentPlayerIndex + 1);
    }
  };

  const getEliminationText = () => {
    if (eliminationType === 'voted') {
      return dict.voted_out;
    } else {
      return dict.eliminated_night;
    }
  };

  return (
    <LastWordsCard>
      <CardHeader>
        <CardTitle>💬 {dict.last_words}</CardTitle>
        <CloseButton onClick={handleNext}>×</CloseButton>
      </CardHeader>

      <PlayerSection>
        <PlayerCard>
          <PlayerAvatarLarge src={currentPlayer.avatar} alt={currentPlayer.nickname} />
          <PlayerNameLarge>{currentPlayerNumber}. {currentPlayer.nickname}</PlayerNameLarge>
          <PlayerNote>{getEliminationText()}</PlayerNote>
        </PlayerCard>

        <ContentSection>
          <MetaInfo style={{  }}>
            {dict.player} №{currentPlayerNumber} - <MetaHighlight>{currentPlayer.nickname}</MetaHighlight> {dict.final_speech}
          </MetaInfo>

          {eliminatedPlayers.length > 1 && (
            <ProgressSection>
              <ProgressLabel>
                {dict.player_of.replace('{0}', String(currentPlayerIndex + 1)).replace('{1}', String(eliminatedPlayers.length))}
              </ProgressLabel>
              <ProgressBar>
                <ProgressFill $progress={progress} />
              </ProgressBar>
            </ProgressSection>
          )}
        </ContentSection>
      </PlayerSection>

      <InstructionsCard>
        <strong>{dict.instructions}</strong>
        <ul>
          <li>{dict.allow_speech}</li>
          <li>{dict.share_thoughts}</li>
          <li>
            {dict.click_continue.replace('{0}', isLastPlayer ? dict.continue_game : dict.next_player)}
          </li>
          {eliminatedPlayers.length > 1 && (
            <li>{dict.multiple_eliminated}</li>
          )}
        </ul>
      </InstructionsCard>

      <ActionButtons>
        <PhaseButton $variant="primary" onClick={handleNext}>
          👏🏼 {dict.goodbye}
        </PhaseButton>
        {onUndo && (
          <UndoButton $variant="secondary" onClick={onUndo}>
            ↶ {eliminationType === 'voted' ? dict.undo_vote : dict.back_to_night}
          </UndoButton>
        )}
      </ActionButtons>
    </LastWordsCard>
  );
};

export default LastWords;
